import DataTable from "../../components/common/DataTable";
import React from "react";
import { Badge, Progress } from "@mantine/core";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { ShieldAlert } from "lucide-react";

const securityIncidents = [
  { key: "1", incidentType: "Unauthorized Access Attempt", severity: "critical", count: 1240, blocked: 1238, blockRate: 99.84, lastOccurred: "2 mins ago" },
  { key: "2", incidentType: "SQL Injection Attack", severity: "critical", count: 380, blocked: 380, blockRate: 100.0, lastOccurred: "15 mins ago" },
  { key: "3", incidentType: "XSS Attack", severity: "high", count: 2100, blocked: 2100, blockRate: 100.0, lastOccurred: "8 mins ago" },
  { key: "4", incidentType: "DDoS Attack", severity: "high", count: 5, blocked: 5, blockRate: 100.0, lastOccurred: "1 hour ago" },
  { key: "5", incidentType: "Brute Force Login", severity: "medium", count: 450, blocked: 448, blockRate: 99.56, lastOccurred: "30 mins ago" },
];

const errorAnalytics = [
  { key: "1", errorCode: "500", errorType: "Internal Server Error", occurrences: 2340, impact: "high", avgResponseTime: "2000ms", status: "resolved" },
  { key: "2", errorCode: "404", errorType: "Not Found", occurrences: 890, impact: "low", avgResponseTime: "150ms", status: "normal" },
  { key: "3", errorCode: "403", errorType: "Forbidden", occurrences: 450, impact: "medium", avgResponseTime: "200ms", status: "investigating" },
  { key: "4", errorCode: "429", errorType: "Too Many Requests", occurrences: 1200, impact: "medium", avgResponseTime: "250ms", status: "monitoring" },
];

const errorTypes = [
  { name: "Server Error (5xx)", value: 35, color: "#ef4444" },
  { name: "Client Error (4xx)", value: 45, color: "#f59e0b" },
  { name: "Timeout", value: 12, color: "#3b82f6" },
  { name: "Network Error", value: 8, color: "#8b5cf6" },
];

const securityColumns = [
  { title: "Incident Type", dataIndex: "incidentType", key: "incidentType" },
  { title: "Severity", dataIndex: "severity", key: "severity", render: (s) => <Badge color={s === "critical" ? "red" : s === "high" ? "orange" : "yellow"} variant="light">{s.toUpperCase()}</Badge> },
  { title: "Total Attempts", dataIndex: "count", key: "count", render: (t) => t.toLocaleString() },
  { title: "Blocked", dataIndex: "blocked", key: "blocked", render: (t) => t.toLocaleString() },
  { title: "Block Rate", dataIndex: "blockRate", key: "blockRate", render: (t) => <Progress value={Math.round(t)} color={t === 100 ? "green" : "yellow"} size="sm" style={{ width: 60 }} /> },
  { title: "Last Occurred", dataIndex: "lastOccurred", key: "lastOccurred" },
];

const errorColumns = [
  { title: "Error Code", dataIndex: "errorCode", key: "errorCode" },
  { title: "Error Type", dataIndex: "errorType", key: "errorType" },
  { title: "Occurrences", dataIndex: "occurrences", key: "occurrences", render: (t) => t.toLocaleString() },
  { title: "Impact", dataIndex: "impact", key: "impact", render: (i) => <Badge color={i === "high" ? "red" : i === "medium" ? "orange" : "green"} variant="light">{i.toUpperCase()}</Badge> },
  { title: "Avg Response", dataIndex: "avgResponseTime", key: "avgResponseTime" },
  { title: "Status", dataIndex: "status", key: "status", render: (s) => <Badge color={s === "resolved" ? "green" : s === "normal" ? "blue" : "orange"} variant="light">{s.toUpperCase()}</Badge> },
];

const securityTrend = [
  { day: "Mon", attempts: 8900, blocked: 8856 },
  { day: "Tue", attempts: 9200, blocked: 9176 },
  { day: "Wed", attempts: 10100, blocked: 10059 },
  { day: "Thu", attempts: 9800, blocked: 9702 },
  { day: "Fri", attempts: 11200, blocked: 11172 },
];

const errorTrend = [
  { hour: "12:00", errors: 245, resolved: 200, pending: 45 },
  { hour: "1:00", errors: 312, resolved: 280, pending: 32 },
  { hour: "2:00", errors: 198, resolved: 180, pending: 18 },
  { hour: "3:00", errors: 425, resolved: 380, pending: 45 },
  { hour: "4:00", errors: 180, resolved: 165, pending: 15 },
];

const ErrorSecurityAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={ShieldAlert}
      title="Error & Security Analytics"
      subtitle="System errors & security incidents monitoring"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Security Threats" value={9370} suffix="" />
        <StatCard title="Blocked" value={9354} suffix="" />
        <StatCard title="Block Rate" value={99.8} suffix="%" />
        <StatCard title="System Errors" value={1360} suffix="" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="Security Threats Timeline">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={securityTrend}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="day" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="attempts" stroke="#ef4444" strokeWidth={2} name="Attempts" />
                <Line type="monotone" dataKey="blocked" stroke="#10b981" strokeWidth={2} name="Blocked" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="System Errors by Hour">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={errorTrend}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="hour" {...chartProps.axis} />
                <YAxis {...chartProps.axis} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="resolved" stackId="a" fill="#10b981" name="Resolved" />
                <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="Error Type Distribution">
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={errorTypes} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name} ${value}%`} labelLine={false}>
                {errorTypes.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" strokeWidth={2} />)}
              </Pie>
              <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="Security Incidents">
        <DataTable dataSource={securityIncidents} columns={securityColumns} pagination={false} scroll={{ x: 1200 }} rowKey="key" />
      </ChartCard>
      </div>

      <ChartCard title="System Error Log">
        <DataTable dataSource={errorAnalytics} columns={errorColumns} pagination={false} scroll={{ x: 1200 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default ErrorSecurityAnalyticsPage;
